import { useTheme } from "next-themes";
import { FileText, Download, Share2, BarChart3, TrendingUp, Users, Calendar } from "lucide-react";
import { PlatformIcon } from "@/components/PlatformIcon";

const reportCards = [
  {
    title: "Weekly Host Performance",
    type: "Generate",
    audience: "Management",
    metrics: ["GMV per host", "Conversion rate", "Avg. session duration"],
    frequency: "Every Monday",
    icon: TrendingUp,
  },
  {
    title: "Brand Campaign Report",
    type: "Generate",
    audience: "Agency → Brand",
    metrics: ["Total impressions", "Products sold", "ROI breakdown"],
    frequency: "Per campaign",
    icon: BarChart3,
  },
  {
    title: "Monthly Operations Summary",
    type: "Generate",
    audience: "C-Level / Stakeholders",
    metrics: ["Revenue trends", "Host rankings", "Platform comparison"],
    frequency: "1st of month",
    icon: Users,
  },
];

export function ReportsMockup() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div className="border border-border bg-card/50 overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-4 md:px-5 py-3 border-b border-border bg-card/80 gap-2">
        <div className="flex items-center gap-3">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-[13px] font-medium text-foreground">Report Center</span>
          <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded">3 templates</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors px-2.5 py-1.5 border border-border rounded">
            <Download className="h-3 w-3" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Report cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {reportCards.map((report, i) => (
          <div key={report.title} className="p-4 md:p-5 group hover:bg-muted/30 transition-colors">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className={`h-8 w-8 rounded flex items-center justify-center ${
                i === 0 ? "bg-success/10 text-success" :
                i === 1 ? "bg-info/10 text-info" :
                "bg-warning/10 text-warning"
              }`}>
                <report.icon className="h-4 w-4" />
              </div>
              <button className="text-[10px] uppercase tracking-wider font-medium px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
                {report.type}
              </button>
            </div>

            <h4 className="text-[14px] font-medium text-foreground mb-1">{report.title}</h4>
            <p className="text-[12px] text-muted-foreground mb-4">For: {report.audience}</p>

            {/* Metrics preview */}
            <div className="space-y-1.5 mb-4">
              {report.metrics.map((metric) => (
                <div key={metric} className="flex items-center gap-2">
                  <div className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                  <span className="text-[11px] text-muted-foreground">{metric}</span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-muted-foreground/60" />
                <span className="text-[11px] text-muted-foreground">{report.frequency}</span>
              </div>
              <div className="flex items-center gap-1">
                <PlatformIcon type="shopee" size={12} />
                <PlatformIcon type="tiktok" size={12} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Preview bar */}
      <div className="border-t border-border px-4 md:px-5 py-3 bg-muted/20">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="h-6 w-20 rounded bg-success/20 flex items-center justify-center">
              <span className="text-[10px] font-medium text-success">+32% GMV</span>
            </div>
            <div className="h-6 w-24 rounded bg-info/20 flex items-center justify-center">
              <span className="text-[10px] font-medium text-info">12 sessions</span>
            </div>
            <div className="h-6 w-20 rounded bg-warning/20 flex items-center justify-center">
              <span className="text-[10px] font-medium text-warning">4 hosts</span>
            </div>
          </div>
          <span className="text-[11px] text-muted-foreground">Last generated: 2 hours ago</span>
        </div>
      </div>
    </div>
  );
}
